import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { adminHeaders, lmsStore, type LmsComp } from "../../lib/lms";
import { useAuth } from "../../context/AuthContext";
import { PopButton } from "../PopButton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui";

const clean = (v: string) => v.replace(/\D/g, "").slice(0, 6);

export function PinInput({ value, onChange, testId, dark }: { value: string; onChange: (v: string) => void; testId: string; dark?: boolean }) {
  return <input inputMode="numeric" autoComplete="off" placeholder="4–6 digits" value={value} onChange={(e) => onChange(clean(e.target.value))}
    className={`field !w-36 !py-2 text-center font-comic !text-lg tracking-[0.3em] ${dark ? "!text-[#0B1F33]" : ""}`} data-testid={testId} />;
}

export function AdminLogin({ comp, onDone }: { comp: LmsComp; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lms/${comp.id}/admin-login`, { pin });
      lmsStore.save({ id: comp.id, name: comp.name, admin: data.admin_token });
      toast.success("Welcome back, admin!");
      setOpen(false); setPin(""); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-xs font-extrabold uppercase tracking-wide text-[#0B1F33]/70 hover:underline" data-testid="lms-im-admin-button"><KeyRound className="h-3.5 w-3.5" strokeWidth={3} /> I'm the admin</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm border-[3px] border-[#0B1F33] bg-[#FFF5E1]" data-testid="lms-admin-login-dialog">
          <DialogHeader>
            <DialogTitle className="font-comic text-2xl">Admin login</DialogTitle>
            <DialogDescription>{comp.has_pin ? `Enter the admin PIN for ${comp.name}.` : "This competition has no admin PIN yet — use the private admin link, or ask the admin to set a PIN."}</DialogDescription>
          </DialogHeader>
          {comp.has_pin && (
            <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (pin.length >= 4) submit(); }}>
              <PinInput value={pin} onChange={setPin} testId="lms-admin-pin-input" />
              <PopButton type="submit" tone="green" disabled={busy || pin.length < 4} data-testid="lms-admin-pin-submit">Unlock</PopButton>
            </form>
          )}
          <p className="text-[11px] font-bold text-[#0B1F33]/60">Dither owner? <Link to="/auth" className="underline">Sign in</Link> to manage every competition.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PinSettings({ comp, onChange }: { comp: LmsComp; onChange: (c: LmsComp) => void }) {
  const [edit, setEdit] = useState(!comp.has_pin);
  const [pin, setPin] = useState("");
  const save = async () => {
    try {
      const { data } = await api.put(`/lms/${comp.id}/pin`, { pin }, adminHeaders(comp.id));
      onChange(data); setPin(""); setEdit(false); toast.success("Admin PIN saved — use it on any device via “I'm the admin”");
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!edit) return <button className="flex items-center gap-1 text-xs font-bold text-white/70 underline" onClick={() => setEdit(true)} data-testid="lms-change-pin"><KeyRound className="h-3.5 w-3.5" /> Change admin PIN</button>;
  return (
    <div className={`space-y-1.5 rounded-xl p-2 ${comp.has_pin ? "bg-white/10" : "bg-[#FF5D1F]"}`} data-testid="lms-pin-settings">
      <p className="text-xs font-extrabold uppercase tracking-wide">{comp.has_pin ? "New admin PIN" : "Set an admin PIN so you never lose admin access"}</p>
      <div className="flex gap-1.5">
        <PinInput value={pin} onChange={setPin} testId="lms-set-pin-input" dark />
        <PopButton size="sm" tone="yellow" disabled={pin.length < 4} onClick={save} data-testid="lms-set-pin-save">Save PIN</PopButton>
        {comp.has_pin && <PopButton size="sm" tone="white" onClick={() => setEdit(false)}>Cancel</PopButton>}
      </div>
    </div>
  );
}

type OwnerRow = { id: string; name: string; players: number; alive: number; status: string; gameweek: number };

export function OwnerCompetitions() {
  const { user } = useAuth();
  const [rows, setRows] = useState<OwnerRow[] | null>(null);
  const owner = !!user && user.role === "admin";
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  useEffect(() => {
    if (!owner) return;
    api.get("/lms/owner/all", { params: { page } }).then((r) => { setTotal(r.data.total); setRows((prev) => [...(page ? prev || [] : []), ...r.data.items]); }).catch(() => setRows((prev) => prev || []));
  }, [owner, page]);
  if (!owner || !rows) return null;
  return (
    <section className="rounded-[22px] border-[3px] border-[#0B1F33] bg-[#0B1F33] p-3 text-white shadow-[0_4px_0_#000]" data-testid="lms-owner-competitions">
      <h2 className="flex items-center gap-2 font-comic text-xl text-[#35E07A]"><ShieldCheck className="h-5 w-5" strokeWidth={3} /> Owner · all competitions ({total})</h2>
      <div className="mt-2 grid max-h-80 gap-1.5 overflow-y-auto sm:grid-cols-2">
        {rows.map((c) => (
          <Link key={c.id} to={`/lms/${c.id}`} className="flex items-center justify-between gap-2 rounded-xl bg-white/10 px-3 py-2 text-sm font-bold hover:bg-white/20" data-testid={`lms-owner-comp-${c.id}`}>
            <span className="truncate">{c.name}</span>
            <span className="shrink-0 text-[10px] uppercase text-white/70">{c.status === "finished" ? "Finished" : `GW${c.gameweek}`} · {c.alive}/{c.players} in</span>
          </Link>
        ))}
      </div>
      {rows.length < total && <button className="mt-2 w-full rounded-xl bg-white/10 py-2 text-xs font-extrabold uppercase hover:bg-white/20" onClick={() => setPage(page + 1)} data-testid="lms-owner-load-more">Show more ({total - rows.length} left)</button>}
    </section>
  );
}
